import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt, hhmm } from "@/lib/api";
import { Button } from "@/components/ui/button";import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ServerConnect } from "@/components/ServerConnect";
import { GiveawayAdmin } from "@/components/GiveawayAdmin";
import { ImageField } from "@/components/ImageField";
import { PodiumAdmin } from "@/components/PodiumAdmin";
import { QuestsAdmin } from "@/components/QuestsAdmin";
import { WatchAdmin } from "@/components/WatchAdmin";
import { AdminShell, NeedsAction, useAdminCounts, useAdminSection } from "@/components/AdminShell";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Plus, Trash2, Pencil, Users, Clock, Coins, Gift } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="border border-border bg-card p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <div className="mono mt-4 text-3xl font-semibold">{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const REWARD_BLANK = {
  name: "", description: "", category: "item", cost: 500, min_level: 1,
  stock: -1, image_url: "", active: true, item_class: "", item_amount: 1, payload: "",
};
const ACH_BLANK = {
  name: "", description: "", icon: "trophy", metric: "minutes",
  threshold: 60, bonus_points: 100, active: true,
};
const OFFER_BLANK = {
  title: "", body: "", kind: "promo", multiplier: 1, bonus_points: 0,
  target: "all", target_level: 1, target_user_id: "", active: true, expires_at: null,
};

function consoleCommand(r) {
  const steam = (r.steam_id || "").replace(/^steam:/, "");
  const sid = r.steam64 || "";
  return (r.payload || "")
    .slice(4)
    .replace(/{sid}/gi, sid || "{sid}")
    .replace(/{steam}/gi, steam || "{steam}")
    .replace(/{license}/gi, (r.license_id || "").replace(/^license:/, "") || "{license}")
    .replace(/{name}/gi, r.username || "{name}");
}

const SELECT = "mt-2 w-full rounded-none border border-input bg-secondary px-3 py-2 text-sm";

const PAYLOAD_KINDS = [
  { kind: "cmd", label: "Console command (Tebex style)", args: [{ ph: "giveitem {id} water 1" }] },
  { kind: "cash", label: "Cash (on hand)", args: [{ ph: "5000", type: "number" }] },
  { kind: "bank", label: "Bank transfer", args: [{ ph: "5000", type: "number" }] },
  { kind: "item", label: "Inventory item", args: [{ ph: "water" }, { ph: "1", type: "number" }] },
  { kind: "vehicle", label: "Vehicle (to garage)", args: [{ ph: "sultanrs" }] },
  { kind: "perk", label: "Custom perk (event)", args: [{ ph: "vip:queue:30" }] },
];

function PayloadBuilder({ value, onChange }) {
  const parts = (value || "").split(":");
  const known = PAYLOAD_KINDS.find((k) => k.kind === parts[0]);
  const [kind, setKind] = useState(known ? known.kind : value ? "perk" : "cash");
  const spec = PAYLOAD_KINDS.find((k) => k.kind === kind) || PAYLOAD_KINDS[0];
  const args =
    kind === "cmd" && known && known.kind === "cmd"
      ? [value.slice(4)]
      : known && known.kind === kind
      ? parts.slice(1)
      : value && kind === "perk"
      ? [value]
      : [];

  useEffect(() => {
    if (known && known.kind !== kind) setKind(known.kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const build = (nextKind, nextArgs) => {
    if (nextKind === "perk") return (nextArgs[0] || "").trim();
    const clean = nextArgs.map((a) => String(a || "").trim()).filter((a) => a !== "");
    return clean.length ? `${nextKind}:${clean.join(":")}` : "";
  };

  return (
    <div className="mt-2 border border-border bg-secondary/40 p-4" data-testid="payload-builder">
      <div className="grid gap-3 md:grid-cols-3">
        <select
          className="rounded-none border border-input bg-secondary px-3 py-2 text-sm"
          data-testid="payload-kind"
          value={kind}
          onChange={(e) => {
            setKind(e.target.value);
            onChange(build(e.target.value, []));
          }}
        >
          {PAYLOAD_KINDS.map((k) => (
            <option key={k.kind} value={k.kind}>{k.label}</option>
          ))}
        </select>
        {spec.args.map((a, i) => (
          <Input
            key={a.ph}
            data-testid={`payload-arg-${i}`}
            className="rounded-none bg-secondary mono"
            type={a.type === "number" ? "number" : "text"}
            placeholder={a.ph}
            value={args[i] ?? ""}
            onChange={(e) => {
              const next = [...args];
              next[i] = e.target.value;
              onChange(build(kind, next));
            }}
          />
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Delivered in-game as <span className="mono text-primary">{value || "—"}</span>
        {kind === "item" && " · item name must match your inventory's item list"}
        {kind === "vehicle" && " · spawn name, parked as owned in the garage"}
        {kind === "perk" && " · fires liquidrp:givePerk for your own handler"}
      </p>
      {kind === "cmd" && (
        <p className="mt-2 text-xs text-muted-foreground">
          Runs it in the server console exactly like a Tebex package does — paste a command you know
          already works. Placeholders: <span className="mono text-primary">{"{id}"}</span> session id,{" "}
          <span className="mono text-primary">{"{sid}"}</span> steam64,{" "}
          <span className="mono text-primary">{"{license}"}</span>,{" "}
          <span className="mono text-primary">{"{cid}"}</span> citizen id,{" "}
          <span className="mono text-primary">{"{name}"}</span>. Separate several commands with{" "}
          <span className="mono text-primary">;</span>. Anything without{" "}
          <span className="mono text-primary">{"{id}"}</span> is delivered even when the player is
          offline.
        </p>
      )}
    </div>
  );
}

function EntityForm({ fields, value, onChange }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {fields.map((f) => (
        <div key={f.key} className={f.wide ? "md:col-span-2" : ""}>
          <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{f.label}</Label>
          {f.type === "select" ? (
            <select
              className={SELECT}
              data-testid={`field-${f.key}`}
              value={value[f.key]}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
            >
              {f.options.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          ) : f.type === "textarea" ? (
            <Textarea
              className="mt-2 rounded-none bg-secondary"
              data-testid={`field-${f.key}`}
              value={value[f.key]}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
            />
          ) : f.type === "switch" ? (
            <div className="mt-3">
              <Switch
                data-testid={`field-${f.key}`}
                checked={!!value[f.key]}
                onCheckedChange={(v) => onChange({ ...value, [f.key]: v })}
              />
            </div>
          ) : f.type === "image" ? (
            <ImageField
              testid={`field-${f.key}`}
              value={value[f.key] || ""}
              onChange={(v) => onChange({ ...value, [f.key]: v })}
            />
          ) : f.type === "payload" ? (
            <PayloadBuilder
              value={value[f.key] || ""}
              onChange={(v) => onChange({ ...value, [f.key]: v })}
            />
          ) : (
            <Input
              className="mt-2 rounded-none bg-secondary"
              data-testid={`field-${f.key}`}
              type={f.type === "number" ? "number" : "text"}
              placeholder={f.placeholder || ""}
              value={value[f.key] ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value,
                })
              }
            />
          )}
          {f.hint && <p className="mt-2 text-xs text-muted-foreground">{f.hint}</p>}
        </div>
      ))}
    </div>
  );
}

function CrudSection({ name, fields, blank, columns, testid }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(blank);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/${name}`);
    setItems(data);
  }, [name]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    try {
      if (editId) await api.put(`/admin/${name}/${editId}`, draft);
      else await api.post(`/admin/${name}`, draft);
      toast.success("Saved");
      setOpen(false);
      setEditId(null);
      setDraft(blank);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/${name}/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <h2 className="text-lg md:text-lg font-semibold capitalize">{name}</h2>
        <Dialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) {
              setEditId(null);
              setDraft(blank);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button className="rounded-none" data-testid={`add-${name}-btn`}>
              <Plus className="mr-2 h-4 w-4" /> New
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-card">
            <DialogHeader>
              <DialogTitle className="capitalize">{editId ? `Edit ${name}` : `New ${name}`}</DialogTitle>
            </DialogHeader>
            <div className="max-h-[65vh] overflow-y-auto pr-1">
              <EntityForm fields={fields} value={draft} onChange={setDraft} />
            </div>
            <Button onClick={save} className="mt-4 w-full rounded-none" data-testid={`save-${name}-btn`}>
              Save
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 border border-border bg-card">
        <div className="grid grid-cols-12 gap-4 border-b border-border px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {columns.map((c) => (
            <div key={c.key} className={`col-span-${c.span || 3}`}>{c.label}</div>
          ))}
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {items.length === 0 && <p className="p-5 text-sm text-muted-foreground">Nothing yet.</p>}
        {items.map((it) => (
          <div key={it.id} className="grid grid-cols-12 items-center gap-4 border-b border-border px-5 py-4 text-sm" data-testid={`${name}-row-${it.id}`}>
            {columns.map((c) => (
              <div key={c.key} className={`col-span-${c.span || 3} truncate`}>
                {c.render ? c.render(it) : String(it[c.key] ?? "")}
              </div>
            ))}
            <div className="col-span-2 flex justify-end gap-2">
              <Button
                size="icon"
                variant="ghost"
                data-testid={`edit-${name}-${it.id}`}
                onClick={() => {
                  const { id, created_at, ...rest } = it;
                  setDraft({ ...blank, ...rest });
                  setEditId(id);
                  setOpen(true);
                }}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" data-testid={`delete-${name}-${it.id}`} onClick={() => remove(it.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then(({ data }) => setA(data));
  }, []);
  if (!a) return <p className="text-sm text-muted-foreground">Loading analytics…</p>;
  return (
    <div data-testid="analytics-section">
      <div className="grid gap-4 md:grid-cols-4">
        <Kpi icon={Users} label="Players" value={fmt(a.players)} sub={`${a.active_7d} active 7d`} testid="kpi-players" />
        <Kpi icon={Clock} label="Total playtime" value={hhmm(a.total_minutes)} sub="tracked via FiveM" testid="kpi-playtime" />
        <Kpi icon={Coins} label="Points issued" value={fmt(a.points_issued)} sub={`${fmt(a.points_outstanding)} outstanding`} testid="kpi-points" />
        <Kpi icon={Gift} label="Redemptions" value={fmt(a.redemptions_total)} sub={`${a.redemptions_pending} pending`} testid="kpi-redemptions" />
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <h3 className="text-lg font-semibold">Playtime, last 14 days</h3>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={a.daily}>
                <CartesianGrid stroke="hsl(213 32% 16%)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8fa3b8" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#8fa3b8" }} />
                <Tooltip contentStyle={{ background: "#0b1320", border: "1px solid #1e2c3f" }} />
                <Line type="monotone" dataKey="minutes" stroke="hsl(210 100% 56%)" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-border bg-card p-6">
          <h3 className="text-lg font-semibold">Top redeemed rewards</h3>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={a.top_rewards}>
                <CartesianGrid stroke="hsl(213 32% 16%)" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#8fa3b8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#8fa3b8" }} />
                <Tooltip contentStyle={{ background: "#0b1320", border: "1px solid #1e2c3f" }} />
                <Bar dataKey="count" fill="hsl(195 100% 60%)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-4 border border-border bg-card p-6">
        <h3 className="text-lg font-semibold">Level distribution</h3>
        <div className="mt-5 grid gap-3 md:grid-cols-5">
          {a.level_distribution.map((l) => (
            <div key={l.name} className="border border-border p-4">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{l.name}</div>
              <div className="mono mt-2 text-2xl text-primary">{l.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Players() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [pts, setPts] = useState(100);
  const [ident, setIdent] = useState({ license_id: "", fivem: "", discord: "", steam: "" });
  const [resetWord, setResetWord] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(null);

  const seasonReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/season-reset", { confirm: resetWord.trim() });
      setResetDone(data);
      setResetWord("");
      toast.success(`Season reset — ${data.players_reset} player(s) back to zero`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResetting(false);
    }
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/players?q=${encodeURIComponent(q)}`);
    setRows(data);
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/players/${id}`);
    setDetail(data);
    setIdent({
      license_id: data.player.license_id || "",
      fivem: data.player.fivem_id || "",
      discord: data.player.discord_id || "",
      steam: data.player.steam_id || "",
    });
  };

  const saveIdentity = async (id) => {
    try {
      await api.post(`/admin/players/${id}/link-online`, ident);
      toast.success("Game identity linked — playtime banks from the next poll");
      load();
      openDetail(id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const adjust = async (id, points) => {
    try {
      await api.post(`/admin/players/${id}/adjust`, { points, reason: "Admin adjustment" });
      toast.success("Balance updated");
      load();
      openDetail(id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="players-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-lg md:text-lg font-semibold">Players</h2>
        <Input
          data-testid="player-search"
          placeholder="Search name, email or license"
          className="w-72 rounded-none bg-secondary"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="mt-5 border border-border bg-card">
        <div className="grid grid-cols-12 gap-4 border-b border-border px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <div className="col-span-3">Player</div>
          <div className="col-span-3">License</div>
          <div className="col-span-2">Level</div>
          <div className="col-span-2">Playtime</div>
          <div className="col-span-2 text-right">Balance</div>
        </div>
        {rows.map((p) => (
          <button
            key={p.id}
            onClick={() => openDetail(p.id)}
            data-testid={`player-row-${p.id}`}
            className="grid w-full grid-cols-12 items-center gap-4 border-b border-border px-5 py-4 text-left text-sm hover:bg-secondary/60"
          >
            <div className="col-span-3 truncate">
              <div className="font-semibold">{p.username}</div>
              <div className="text-xs text-muted-foreground">{p.email}</div>
            </div>
            <div className="col-span-3 mono truncate text-xs text-muted-foreground">{p.license_id || "—"}</div>
            <div className="col-span-2">
              <Badge className="rounded-none">L{p.level.level} {p.level.name}</Badge>
            </div>
            <div className="col-span-2 mono">{hhmm(p.playtime_minutes)}</div>
            <div className="col-span-2 mono text-right text-primary">{fmt(p.points_balance)}</div>
          </button>
        ))}
        {rows.length === 0 && <p className="p-5 text-sm text-muted-foreground">No players found.</p>}
      </div>

      <div className="mt-6 border border-destructive/40 bg-card p-6" data-testid="season-reset-card">
        <h3 className="text-lg font-semibold text-destructive">Reset the season</h3>
        <p className="mt-2 max-w-2xl text-xs text-muted-foreground">
          Sends every player back to level 1 with 0 points and 0 playtime, and clears session
          history, the points ledger, quest progress, giveaway entries and past winners. Accounts,
          Discord links and pending reward redemptions are kept. A snapshot of every balance is
          saved first, so this can be undone by support if you change your mind.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Input
            data-testid="season-reset-confirm"
            placeholder="Type RESET"
            className="mono w-48 rounded-none bg-secondary"
            value={resetWord}
            onChange={(e) => setResetWord(e.target.value)}
          />
          <Button
            variant="outline"
            className="rounded-none border-destructive/50 text-destructive hover:bg-destructive/10"
            disabled={resetWord.trim().toUpperCase() !== "RESET" || resetting}
            onClick={seasonReset}
            data-testid="season-reset-btn"
          >
            {resetting ? "Resetting…" : "Wipe beta progress"}
          </Button>
          {resetDone && (
            <span className="text-xs text-primary" data-testid="season-reset-result">
              {resetDone.players_reset} player(s) reset · snapshot {resetDone.snapshot_id.slice(0, 8)}
            </span>
          )}
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl bg-card" data-testid="player-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.player.username}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 md:grid-cols-4 text-sm">
                <div><div className="text-xs text-muted-foreground">Balance</div><div className="mono text-primary">{fmt(detail.player.points_balance)}</div></div>
                <div><div className="text-xs text-muted-foreground">Lifetime</div><div className="mono">{fmt(detail.player.lifetime_points)}</div></div>
                <div><div className="text-xs text-muted-foreground">Playtime</div><div className="mono">{hhmm(detail.player.playtime_minutes)}</div></div>
                <div><div className="text-xs text-muted-foreground">Level</div><div className="mono">L{detail.level.level}</div></div>
              </div>
              <div className="mt-4 flex items-end gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Adjust points</Label>
                  <Input
                    type="number"
                    data-testid="adjust-points-input"
                    className="mt-2 w-40 rounded-none bg-secondary"
                    value={pts}
                    onChange={(e) => setPts(Number(e.target.value))}
                  />
                </div>
                <Button className="rounded-none" data-testid="adjust-points-btn" onClick={() => adjust(detail.player.id, pts)}>
                  Apply
                </Button>
              </div>
              <div className="mt-6 border-t border-border pt-6" data-testid="identity-editor">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Game identity — link this player's in-game account
                </Label>
                <p className="mt-2 text-xs text-muted-foreground">
                  Paste any identifier from Fini / your server console. Matching runs
                  fivem → discord → license → steam, so one is enough.
                </p>
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  {[
                    ["license_id", "FiveM license"],
                    ["fivem", "Cfx.re / fivem id"],
                    ["discord", "In-game Discord id"],
                    ["steam", "Steam id"],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
                      <Input
                        data-testid={`identity-${key}-input`}
                        className="mt-2 rounded-none bg-secondary mono text-xs"
                        value={ident[key]}
                        onChange={(e) => setIdent({ ...ident, [key]: e.target.value })}
                      />
                    </div>
                  ))}
                </div>
                <Button
                  className="mt-4 rounded-none"
                  data-testid="save-identity-btn"
                  onClick={() => saveIdentity(detail.player.id)}
                >
                  Save identity
                </Button>
                {detail.player.steam64 && (
                  <div className="mono mt-4 border border-border bg-secondary/40 p-4 text-xs" data-testid="steam64-line">
                    <span className="text-muted-foreground">steam64 for Tebex-style commands: </span>
                    <span className="text-primary">{detail.player.steam64}</span>
                    <span className="text-muted-foreground"> (hex {detail.player.steam_id})</span>
                  </div>
                )}
              </div>

              <div className="mt-6 max-h-64 overflow-auto border border-border">
                {detail.transactions.map((t) => (
                  <div key={t.id} className="flex justify-between border-b border-border px-4 py-2 text-sm">
                    <span>{t.note}</span>
                    <span className={`mono ${t.points >= 0 ? "text-primary" : "text-destructive"}`}>{t.points}</span>
                  </div>
                ))}
                {detail.transactions.length === 0 && <p className="p-4 text-sm text-muted-foreground">No transactions.</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Redemptions() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/redemptions");
    setRows(data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/redemptions/${id}/status?status=${status}`);
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="redemptions-section">
      <h2 className="text-lg md:text-lg font-semibold">Redemption queue</h2>
      <div className="mt-5 border border-border bg-card">
        {rows.length === 0 && <p className="p-5 text-sm text-muted-foreground">Queue empty.</p>}
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-4 border-b border-border px-5 py-4 text-sm" data-testid={`admin-redemption-${r.id}`}>
            <div>
              <div className="font-semibold">{r.reward_name}</div>
              <div className="text-xs text-muted-foreground">
                {r.username} · <span className="mono">{r.license_id || "no license"}</span> · code{" "}
                <span className="mono text-primary">{r.code}</span> · class{" "}
                <span className="mono">{r.item_class || r.payload || r.category || "—"}</span>
                {r.item_amount > 1 && <> × {r.item_amount}</>}
              </div>
              {r.payload?.startsWith("cmd:") && (
                <div className="mono mt-1 break-all text-xs text-primary" data-testid={`console-command-${r.id}`}>
                  console: {consoleCommand(r)}
                </div>
              )}
              {r.last_error && (
                <div className="mono mt-1 text-xs text-destructive" data-testid={`redemption-error-${r.id}`}>
                  delivery failed: {r.last_error}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Badge className="rounded-none uppercase" variant={r.status === "fulfilled" ? "default" : r.status === "rejected" ? "destructive" : "secondary"}>
                {r.status}
              </Badge>
              {r.status === "pending" && (
                <>
                  <Button size="sm" className="rounded-none" data-testid={`fulfill-${r.id}`} onClick={() => setStatus(r.id, "fulfilled")}>
                    Fulfill
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none" data-testid={`reject-${r.id}`} onClick={() => setStatus(r.id, "rejected")}>
                    Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LevelsConfig() {
  const [levels, setLevels] = useState([]);
  const [key, setKey] = useState(null);

  useEffect(() => {
    api.get("/admin/levels").then(({ data }) => setLevels(data));
    api.get("/admin/server-key").then(({ data }) => setKey(data));
  }, []);

  const save = async () => {
    try {
      await api.put("/admin/levels", { levels });
      toast.success("Levels saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const update = (i, field, v) => {
    const copy = [...levels];
    copy[i] = { ...copy[i], [field]: field === "name" || field === "perk" ? v : Number(v) };
    setLevels(copy);
  };

  return (
    <div data-testid="levels-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg md:text-lg font-semibold">Levels & server integration</h2>
        <Button className="rounded-none" onClick={save} data-testid="save-levels-btn">Save levels</Button>
      </div>

      <div className="mt-5 border border-border bg-card p-6">
        <div className="space-y-3">
          {levels.map((l, i) => (
            <div key={l.level} className="grid grid-cols-12 items-center gap-3" data-testid={`level-row-${l.level}`}>
              <span className="mono col-span-1 text-primary">L{l.level}</span>
              <Input className="col-span-3 rounded-none bg-secondary" value={l.name} onChange={(e) => update(i, "name", e.target.value)} data-testid={`level-name-${l.level}`} />
              <Input type="number" className="col-span-3 rounded-none bg-secondary mono" value={l.min_points} onChange={(e) => update(i, "min_points", e.target.value)} data-testid={`level-points-${l.level}`} />
              <Input className="col-span-5 rounded-none bg-secondary" value={l.perk} onChange={(e) => update(i, "perk", e.target.value)} data-testid={`level-perk-${l.level}`} />
            </div>
          ))}
        </div>
      </div>

      {key && (
        <div className="mt-4 border border-border bg-card p-6" data-testid="server-key-card">
          <h3 className="text-lg font-semibold">FiveM ingest</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Your server resource posts one heartbeat per minute per player with header{" "}
            <span className="mono text-primary">X-Server-Key</span>.
          </p>
          <pre className="mono mt-4 overflow-auto border border-border bg-secondary p-4 text-xs">
{`POST ${process.env.REACT_APP_BACKEND_URL}/api/ingest/heartbeat
X-Server-Key: ${key.server_key}
{ "license_id": "license:abc123", "minutes": 1 }

GET  /api/ingest/pending?license_id=license:abc123
POST /api/ingest/fulfill/{redemption_id}`}
          </pre>
          <p className="mt-3 text-xs text-muted-foreground">
            Drop-in Lua resource is in the repo at <span className="mono">/app/fivem_resource/liquidrp_loyalty</span>.
          </p>
        </div>
      )}
    </div>
  );
}

const SECTION_META = {
  overview: ["Dashboard", "Live playtime, points and everything awaiting your call"],
  players: ["Players", "Search accounts, adjust points and link in-game identities"],
  quests: ["Quest portal", "Brands, campaigns, quest builder, proof review and CPA revenue"],
  rewards: ["Rewards & queue", "Reward catalogue plus the redemption fulfilment queue"],
  achievements: ["Achievements", "Milestone badges and their bonus payouts"],
  offers: ["Offers", "Promos, multipliers and targeted bonuses"],
  levels: ["Levels", "Level thresholds and the perks attached to each"],
  podium: ["Weekly podium", "Top-three playtime rewards for the week"],
  giveaway: ["Giveaway", "Draw config, entries, winners and claims"],
  watch: ["Watch & earn", "Nominate up to four creator streams and set the point rate"],
  connect: ["Connect & resource", "Fini Apollo, Discord and the FiveM delivery resource"],
};

function RewardsSection() {
  const [tab, setTab] = useState("catalogue");
  return (
    <div data-testid="rewards-section">
      <div className="flex flex-wrap gap-2">
        {[["catalogue", "Catalogue"], ["queue", "Redemption queue"]].map(([v, l]) => (
          <button
            key={v}
            type="button"
            onClick={() => setTab(v)}
            data-testid={`rewards-tab-${v}`}
            className={`border px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
              tab === v
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "queue" ? (
          <Redemptions />
        ) : (
          <CrudSection
            name="rewards"
            testid="rewards-admin"
            blank={REWARD_BLANK}
            fields={[
              { key: "name", label: "Name" },
              { key: "category", label: "Category", type: "select", options: ["cash", "vehicle", "food", "item", "cosmetic", "vip"] },
              { key: "description", label: "Description", type: "textarea", wide: true },
              { key: "cost", label: "Cost (points)", type: "number" },
              { key: "min_level", label: "Min level", type: "number" },
              { key: "stock", label: "Stock (-1 = unlimited)", type: "number" },
              { key: "item_class", label: "Item class (in-game)", placeholder: "cash_clean",
                hint: "Your devs map this class to the actual in-game item. Sent with the confirmation code when a player types /redeem." },
              { key: "item_amount", label: "Amount / quantity", type: "number",
                hint: "Passed alongside the class — cash value, item count, etc." },
              { key: "payload", label: "Legacy payload (optional)", type: "payload", wide: true },
              { key: "image_url", label: "Thumbnail", type: "image", wide: true },
              { key: "active", label: "Active", type: "switch" },
            ]}
            columns={[
              { key: "name", label: "Reward", span: 4 },
              { key: "item_class", label: "Item class", span: 3, render: (r) => <span className="mono text-xs">{r.item_class || "—"}</span> },
              { key: "cost", label: "Cost", span: 2, render: (r) => <span className="mono">{fmt(r.cost)}</span> },
              { key: "min_level", label: "Lv", span: 1 },
              { key: "active", label: "Active", span: 1, render: (r) => (r.active ? "Yes" : "No") },
            ]}
          />
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [active, setActive] = useAdminSection();
  const [counts, refreshCounts] = useAdminCounts();
  const [title, subtitle] = SECTION_META[active] || SECTION_META.overview;

  const go = (key) => {
    setActive(key);
    refreshCounts();
  };

  return (
    <AdminShell active={active} onNavigate={go} counts={counts} title={title} subtitle={subtitle}>
      {active === "overview" && (
        <div className="space-y-8">
          <NeedsAction counts={counts} onNavigate={go} />
          <Analytics />
        </div>
      )}
      {active === "players" && <Players />}
      {active === "quests" && <QuestsAdmin />}
      {active === "rewards" && <RewardsSection />}
      {active === "achievements" && (
        <CrudSection
          name="achievements"
          testid="achievements-admin"
          blank={ACH_BLANK}
          fields={[
            { key: "name", label: "Name" },
            { key: "metric", label: "Metric", type: "select", options: ["minutes", "points_earned", "redemptions", "sessions"] },
            { key: "description", label: "Description", type: "textarea", wide: true },
            { key: "threshold", label: "Threshold", type: "number" },
            { key: "bonus_points", label: "Bonus points", type: "number" },
            { key: "icon", label: "Icon name" },
            { key: "active", label: "Active", type: "switch" },
          ]}
          columns={[
            { key: "name", label: "Achievement", span: 4 },
            { key: "metric", label: "Metric", span: 3 },
            { key: "threshold", label: "Threshold", span: 2, render: (a) => <span className="mono">{fmt(a.threshold)}</span> },
            { key: "bonus_points", label: "Bonus", span: 1 },
          ]}
        />
      )}
      {active === "offers" && (
        <CrudSection
          name="offers"
          testid="offers-admin"
          blank={OFFER_BLANK}
          fields={[
            { key: "title", label: "Title" },
            { key: "kind", label: "Kind", type: "select", options: ["promo", "multiplier", "bonus"] },
            { key: "body", label: "Body", type: "textarea", wide: true },
            { key: "multiplier", label: "Multiplier", type: "number" },
            { key: "bonus_points", label: "Bonus points", type: "number" },
            { key: "target", label: "Target", type: "select", options: ["all", "level", "user"] },
            { key: "target_level", label: "Target level", type: "number" },
            { key: "target_user_id", label: "Target user id", wide: true },
            { key: "active", label: "Active", type: "switch" },
          ]}
          columns={[
            { key: "title", label: "Offer", span: 5 },
            { key: "kind", label: "Kind", span: 2 },
            { key: "target", label: "Target", span: 2 },
            { key: "active", label: "Active", span: 1, render: (o) => (o.active ? "Live" : "Off") },
          ]}
        />
      )}
      {active === "levels" && <LevelsConfig />}
      {active === "podium" && <PodiumAdmin />}
      {active === "giveaway" && <GiveawayAdmin />}
      {active === "watch" && <WatchAdmin />}
      {active === "connect" && <ServerConnect />}
    </AdminShell>
  );
}
